"use client";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  Text,
  Image,
  Box,
  Grid,
  Flex,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import {
  useGetImagePrompt,
  useGetImgGenModePrice,
  useFalForUpscale,
  useGetModelById,
  useDeleteImage,
  useGetModelCreation,
} from "queries";
import { FaRegArrowAltCircleUp, FaDownload } from "react-icons/fa";
import { IoCopy } from "react-icons/io5";
import { useImageSize } from "react-image-size";
import { useEffect, useState, useRef } from "react";
import { useAppContext, useWindowSize } from "hooks";
import { WSS_IMG_GEN } from "api";
import { FaTrash } from "react-icons/fa";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { useQueryClient } from "@tanstack/react-query";

interface UpscaleFromImageRequest {
  file_name: string;
}

interface PromptModalProps {
  file_name: string;
  generationImage: string;
  model_id: string;
  isFlux?: boolean;
  isOpen: boolean;
  storage_type?: string;
  onClose: () => void;
}

const PromptModal: React.FC<PromptModalProps> = ({
  file_name,
  generationImage,
  model_id,
  isFlux = false,
  isOpen,
  storage_type = "s3",
  onClose,
}) => {
  const queryClient = useQueryClient()
  const { jwt, profile } = useAppContext();
  const toast = useToast();
  const { data: model, isLoading: isModelLoading } = useGetModelById(model_id);
  const { data: imgGenModePrice } = useGetImgGenModePrice();
  const { mutateAsync: upscaleFal } = useFalForUpscale();
  const [isWebSocketProcessing, setIsWebSocketProcessing] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [creditsRequired, setCreditsRequired] = useState<number>(1);
  const [isInsufficientBalance, setIsInsufficientBalance] =
    useState<boolean>(false);
  const [upscalingImageFileName, setUpscalingImageFileName] = useState<
    string | null
  >(null);
  const {
    data: generations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetModelCreation(8, model_id);
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (generations?.pages) {
      const allImages = generations.pages.flatMap(
        (page: any) => page.image_generations
      );
      return allImages.findIndex(img => img.file_name === file_name);
    }
    return -1;
  });
  const [currentFileName, setCurrentFileName] = useState(file_name);
  const [currentImage, setCurrentImage] = useState(generationImage);
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };
  const { isMobile } = useWindowSize();
  const {
    isOpen: isDeleteOpen,
    onOpen: deleteOpen,
    onClose: deleteClose,
  } = useDisclosure();
  const deleteImage = useDeleteImage();
  const { data, isLoading, refetch } = useGetImagePrompt(
    currentFileName,
    isFlux,
    {
      enabled: isOpen,
    }
  );
  const [dimensions] = useImageSize(generationImage ?? "");
  const [selectedImageAspectRatio, setSelectedImageAspectRatio] =
    useState<number>(2 / 3);
  const originalFileName = file_name;
  const originalImage = generationImage;


  useEffect(() => {
    setCreditsRequired(imgGenModePrice?.upscale ?? 2);
  }, [imgGenModePrice]);

  useEffect(() => {
    if (dimensions) {
      setSelectedImageAspectRatio(dimensions.width / dimensions.height);
    } else {
      setSelectedImageAspectRatio(2 / 3);
    }
  }, [dimensions]);

  useEffect(() => {
    setIsInsufficientBalance(profile?.credit! - creditsRequired < 0);
  }, [profile, creditsRequired]);



  useEffect(() => {
    if (!isOpen) {
      setCurrentFileName(originalFileName);
      setCurrentImage(originalImage);
      setCurrentIndex(0);
      return;
    }
  
    // Set initial state when modal opens
    if (generations?.pages) {
      const allImages = generations.pages.flatMap(
        (page: any) => page.image_generations
      );
      const matchingIndex = allImages.findIndex(img => img.file_name === file_name);
      
      console.log('Setting modal state:', {
        matchingIndex,
        fileName: file_name,
        totalImages: allImages.length,
        generationImage,
        storage_type,
        isFlux
      });
      
      if (matchingIndex !== -1) {
        setCurrentIndex(matchingIndex);
        setCurrentFileName(file_name);
        setCurrentImage(isFlux  && storage_type === "r2" ? generationImage : isFlux ? file_name : generationImage);
      }
    }
  }, [isOpen]);

  const handleNavigation = async (direction: "prev" | "next") => {
    if (!generations?.pages) return;
    
    const allImages = generations.pages.flatMap(
      (page: any) => page.image_generations
    );
    
    const newIndex = direction === "prev" ? currentIndex - 1 : currentIndex + 1;

  
    if (newIndex >= 0 && newIndex < allImages.length) {
      if (
        direction === "next" &&
        newIndex === allImages.length - 1 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        await fetchNextPage();
        
        const updatedImages = generations.pages.flatMap(
          (page: any) => page.image_generations
        );
        const newImage = updatedImages[newIndex];
  
        setImageUrl(null);
        setUpscalingImageFileName(null);
        setCurrentIndex(newIndex);
        setCurrentFileName(newImage.file_name);
        setCurrentImage(isFlux && storage_type !== "r2" ? newImage.file_name : newImage.sas_url);
        return;
      }
  
      const newImage = allImages[newIndex];
      setImageUrl(null);
      setUpscalingImageFileName(null);
      setCurrentIndex(newIndex);
      setCurrentFileName(newImage.file_name);
      setCurrentImage(isFlux && storage_type !== "r2" ? newImage.file_name : newImage.sas_url);
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "Yesterday";
    return `${diffInDays} days ago`;
  };

  const generateImages = async (
    localUpscalingImageFileName: string,
    localUpscalingImageAspectRatio: number
  ) => {
    if (isInsufficientBalance) {
      toast({
        title: "Insufficient Credits",
        description: "You do not have enough credits to upscale this image.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setUpscalingImageFileName(localUpscalingImageFileName);
    // Check if it's a Flux model
    if (model?.isFlux) {
      try {
        setIsWebSocketProcessing(true);
        let data = await upscaleFal({
          input_filename: localUpscalingImageFileName,
          model_id: model_id,
        });
        toast({
          title: "Upscale Successful",
          description: "Your image has been successfully upscaled.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setImageUrl(data.image_url);
        refetch();
      } catch (error) {
        console.error("Error upscaling image:", error);
        toast({
          title: "Upscale Failed",
          description: "There was an error upscaling the image.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsWebSocketProcessing(false);
      }
    } else {
      const payloadToSend: UpscaleFromImageRequest = {
        file_name: localUpscalingImageFileName,
      };

      setIsWebSocketProcessing(true);

      const socket = new WebSocket(WSS_IMG_GEN(jwt, "upscale"));
      let isConnectionEstablished = false;
      let firstImage = true;

      socket.onopen = () => {
        console.log("WebSocket connection opened");
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (
            data.message === "image generation connection established" &&
            !isConnectionEstablished
          ) {
            isConnectionEstablished = true;
            socket.send(JSON.stringify(payloadToSend));
          } else if (
            data.message === "image generation progress" ||
            data.message === "image generation result"
          ) {
            if (firstImage) {
              firstImage = false;
              refetch();
            }
            const b64Image = data.b64_image;
            setImageUrl(b64Image);
            queryClient.invalidateQueries({
              queryKey: ["generation", model_id]
            });
          }
        } catch (e) {
          console.error("Error parsing message", e);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket Error:", error);
        setImageUrl(null);
        setUpscalingImageFileName(null);
        setIsWebSocketProcessing(false);
      };

      socket.onclose = (event) => {
        if (event.code !== 1000) {
          if (event.code === 4002) {
            setIsInsufficientBalance(true);
            toast({
              title: "Insufficient Credits",
              description: "You do not have enough credits to continue.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          } else {
            toast({
              title: "Error",
              description: event.reason,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        }

        setIsWebSocketProcessing(false);
      };
    }
  
  };
  const handleDelete = async () => {
    try {
      await deleteImage.mutateAsync(
        { file_name: currentFileName },
        {
          onSuccess: async () => {
            deleteClose();
            
            if (generations?.pages) {
              const allImages = generations.pages.flatMap(
                (page: any) => page.image_generations
              ).filter(img => img.file_name !== currentFileName);
              
              let nextImage;
              if (currentIndex > 0) {
                nextImage = allImages[currentIndex - 1];
              } else if (allImages.length > 0) {
                nextImage = allImages[0];
              } else {
                nextImage = {
                  file_name: originalFileName,
                  sas_url: originalImage
                };
              }
              
              setCurrentFileName(nextImage.file_name);
              setCurrentImage(isFlux && nextImage.storage_type !== "r2" ? nextImage.file_name : nextImage.sas_url);
              setCurrentIndex(Math.max(0, currentIndex - 1));
            }
            
            setUpscalingImageFileName(null);
            setImageUrl(null);
            await queryClient.invalidateQueries({
              queryKey: ["generation", model_id]
            });
            
            toast({
              title: "Success",
              description: "Image deleted successfully",
              status: "success",
              duration: 3000,
              isClosable: true,
            });
          },
        }
      );
    } catch (error: any) {
      console.error("Error deleting image:", error);
      toast({
        title: "Error",
        description: "Failed to delete image",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const descriptiveName = currentFileName
  .split("/")
  .pop()
  ?.replace(/_\d+(_generated)?\.\w+$/, "")
  .replace(/_/g, " ");

  const isRealisticPrompt = descriptiveName
    ?.toLowerCase()
    .startsWith("realistic");

  const extractOptions = (name: string) => {
    const options: Record<string, string> = {};

    if (name.includes("in a")) {
      options["Themes"] =
        name
          .match(/in a ([^,]+)/)?.[1]
          ?.replace(" setting", "")
          ?.replace(/\b\w/g, (c) => c.toUpperCase()) || "";
    }

    if (name.includes("wearing")) {
      options["Outfit"] =
        name
          .match(/wearing ([^,]+)/)?.[1]
          ?.replace(/\b\w/g, (c) => c.toUpperCase()) || "";
    }

    if (name.includes("with")) {
      options["Hair Color"] =
        name
          .match(/with ([^,]+)/)?.[1]
          ?.replace(" hair color", "")
          ?.replace(/\b\w/g, (c) => c.toUpperCase()) || "";
    }

    return options;
  };

  const renderBackButton = (mobileRender: boolean) => (
    <Button
      variant="ghost"
      color="white"
      _hover={{ bg: "whiteAlpha.200" }}
      display={
        mobileRender
          ? { base: "block", md: "none" }
          : { base: "none", md: "block" }
      }
      isDisabled={currentIndex === 0}
      onClick={() => handleNavigation("prev")}
    >
      <IoChevronBack size={mobileRender ? 15 : 40} />
    </Button>
  );
  const renderNextButton = (mobileRender: boolean) => {
    if (!generations?.pages) return null;
    
    const allImages = generations.pages.flatMap(
      (page: any) => page.image_generations
    );
    
    const isLastImage = currentIndex === allImages.length - 1;
    const canNavigateNext = !isLastImage || hasNextPage;

    return (
      <Button
        variant="ghost"
        color="white"
        _hover={{ bg: "whiteAlpha.200" }}
        display={
          mobileRender
            ? { base: "block", md: "none" }
            : { base: "none", md: "block" }
        }
        isDisabled={!canNavigateNext}
        onClick={() => handleNavigation("next")}
      >
        {isFetchingNextPage ? (
          <Spinner size="sm" color="white" />
        ) : (
          <IoChevronForward size={mobileRender ? 15 : 40} />
        )}
      </Button>
    );
  };
  
  const handleModalClose = () => {
    setUpscalingImageFileName(null);
    onClose();
  };
  const realisticOptions =
    isRealisticPrompt && descriptiveName
      ? extractOptions(descriptiveName)
      : null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => handleModalClose()}
        isCentered
        size="full"
      >
        <ModalOverlay />
        <ModalContent
          bg="rgba(0,0,0,0.9)"
          overflow={isMobile ? "auto" : "hidden"}
          h={isMobile ? "100vh" : ""}
        >
          <ModalCloseButton
            mr={isMobile ? "15px" : "40px"}
          />
          <Box  px={{ base: "4", md: "12" }} py={{ base: "2", md: "4" }}>
            <Flex w="100%">
              <Text color="#787878" fontSize="sm">
                Created {data?.created_at && getTimeAgo(data.created_at)}
              </Text>
            </Flex>
            <Flex mt={isMobile ? "5px" : 0} mb={isMobile ? 1: 4} display={{ base: "flex", md: "none" }} gap={4}>
              {data?.upscaled && (
                <Flex
                  bg="#712EFF"
                  borderRadius="12px"
                  align="center"
                  justify="center"
                >
                  <Text py="8px" px="10px" color="white">
                    HD
                  </Text>
                </Flex>
              )}
              {isFlux && (
                <Flex
                  bg="pink"
                  borderRadius="12px"
                  align="center"
                  justify="center"
                >
                  <Text py="8px" px="10px" color="white">
                    Flux
                  </Text>
                </Flex>
              )}
              {data?.img2img && !data?.upscaled && (
                <Flex
                  bg="#FF1B6D"
                  borderRadius="12px"
                  align="center"
                  justify="center"
                >
                  <Text py="8px" px="10px" color="white">
                    Remix
                  </Text>
                </Flex>
              )}
            </Flex>

            <Flex
              h="100%"
              align="center"
              direction={isMobile ? "column" : "row"}
              gap={isMobile ? 2 : 8}
            >
              {/* Left navigation button */}
              <Flex w="50px" alignSelf="center" align="center">
                {!isMobile && renderBackButton(false)}
              </Flex>

              {/*Left column */}
              <Flex
                w="100%"
                direction="column"
                align="center"
                justify="center"
                minW="45vw"
                flex="1"
              >
                <Flex h="100%" align="center" direction="column"flex="1" >
                  {upscalingImageFileName === currentFileName ? (
                    imageUrl ? (
                      <Flex w="100%" align="center">
                        {isMobile && renderBackButton(true)}

                        {isMobile ? (
                            <Image
                              borderRadius="12px"
                              src={currentImage || data?.image_url}
                              maxH="80vh"
                              maxW="80vw"
                            />
                          ) : (
                            <Image
                              borderRadius="12px"
                              src={currentImage || data?.image_url}
                              objectFit="contain"
                              maxW="100%"
                              maxH="70vh"
                              w="auto"
                              h="auto"
                            />
                          )}
                        {renderNextButton(true)}
                      </Flex>
                    ) : (
                      <Flex
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        w="100%"
                        h="100%"
                        minH="80vh"
                      >
                        <Spinner
                          thickness="4px"
                          speed="0.65s"
                          emptyColor="gray.200"
                          color="button.500"
                          size="xl"
                        />
                        <Text mt={3} fontSize="lg" color="gray.500">
                          Upscaling
                        </Text>
                        <Text mt={3} fontSize="md" color="white">
                          This might take a little while
                        </Text>
                        <Text fontSize="md" color="white">
                          You can close this window
                        </Text>
                      </Flex>
                    )
                  ) : (
                    <Flex justify="center" align="center">
                      {isMobile && renderBackButton(true)}

                      {isMobile ? (
                        <Image
                          borderRadius="12px"
                          src={currentImage || data?.image_url}
                          maxH="80vh"
                          maxW="80vw"
                        />
                      ) : (
                        <Image
                          borderRadius="12px"
                          src={currentImage || data?.image_url}
                          objectFit="contain"
                          maxW="100%"
                          maxH="70vh"
                          w="auto"
                          h="auto"
                        />
                      )}
                      {renderNextButton(true)}
                    </Flex>
                  )}
                </Flex>
              </Flex>

              {/* Right column */}
              <Flex
                h="100%"
                justify="start"
                direction="column"
                alignSelf="stretch"
                flex="2"
                gap={6}
              >
                <Flex display={{ base: "none", md: "flex" }} gap={4}>
                  {data?.upscaled && (
                    <Flex
                      bg="#712EFF"
                      borderRadius="12px"
                      align="center"
                      justify="center"
                    >
                      <Text py="8px" px="10px" color="white">
                        HD
                      </Text>
                    </Flex>
                  )}
                  {isFlux && (
                    <Flex
                      bg="#FF00FF"
                      borderRadius="12px"
                      align="center"
                      justify="center"
                    >
                      <Text py="8px" px="10px" color="white">
                        Flux
                      </Text>
                    </Flex>
                  )}
                  {data?.img2img && !data?.upscaled && (
                    <Flex
                      bg="#FF1B6D"
                      borderRadius="12px"
                      align="center"
                      justify="center"
                    >
                      <Text py="8px" px="10px" color="white">
                        Remix
                      </Text>
                    </Flex>
                  )}
                </Flex>

                {realisticOptions?.lenght || data?.options?.length ? (
                  <ModalHeader color="#FF1B6D" px="0">
                    Options used
                  </ModalHeader>
                ) : null}

                {realisticOptions?.lenght ? (
                  <Grid
                    templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                    gap={6}
                  >
                    {Object.entries(realisticOptions).map(
                      ([category, value], index) => (
                        <Box key={index}>
                          <Text color="white" fontWeight={600} fontSize="16px">
                            {category}
                          </Text>
                          <Text color="white">{value}</Text>
                        </Box>
                      )
                    )}
                  </Grid>
                ) : data?.options && data?.options.length > 0 ? (
                  <Grid
                    templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                    gap={isMobile ? 2 : 6}
                  >
                    {data.options.map((option, index) => (
                      <Box key={index}>
                        <Text color="white" fontWeight={600} fontSize="16px">
                          {option?.option_category_name}
                        </Text>
                        <Text color="white">{option?.name}</Text>
                      </Box>
                    ))}
                  </Grid>
                ) : data && data?.prompt && !data?.upscaled ||
                  (data && descriptiveName && isFlux && !data?.upscaled) ? (
                  <Box>
                    <Text
                      color="#FF1B6D"
                      fontSize="16px"
                      fontWeight={600}
                      mb="2"
                    >
                      Prompt
                    </Text>
                    <Box
                      position="relative"
                      bg="black"
                      p="4"
                      borderRadius="5px"
                      border="1px solid"
                      borderColor="white"
                      mb="4"
                      h="100%"
                    >
                      <Text color="white">
                        {data?.prompt ? data?.prompt : isFlux ? descriptiveName : ""}
                      </Text>
                      <Button
                        position="absolute"
                        bottom="2"
                        right="2"
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          copyToClipboard(
                            data?.prompt ? data?.prompt : isFlux && descriptiveName ? descriptiveName : ""
                          )
                        }
                        aria-label="Copy prompt"
                      >
                        <IoCopy color="#808080" />
                      </Button>
                    </Box>
                    {!isFlux && (
                      <>
                        {" "}
                        <Text
                          color="#FF1B6D"
                          fontSize="16px"
                          fontWeight={600}
                          mb="2"
                        >
                          Negative Prompt
                        </Text>
                        <Box
                          position="relative"
                          bg="black"
                          p="4"
                          borderRadius="5px"
                          border="1px solid"
                          borderColor="white"
                        >
                          <Text color="white">
                            {data?.custom_negative_prompt}
                          </Text>
                          <Button
                            position="absolute"
                            bottom="2"
                            right="2"
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              copyToClipboard(
                                data?.custom_negative_prompt || ""
                              )
                            }
                            aria-label="Copy negative prompt"
                          >
                            <IoCopy color="#808080" />
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                ) : isLoading ? (
                  <Text color="white">Loading...</Text>
                ) : !data?.upscaled ? (
                  <Text color="white" fontSize="18px">
                    <Text as="span" fontWeight="bold">
                      Prompt:
                    </Text>{" "}
                    {data?.prompt ? data?.prompt : isFlux ?  descriptiveName : ""}
                  </Text>
                ) : (
                  <Text>This image was upscaled!</Text>
                )}
                <Flex w="100%" align="center" justify="start" gap={4} mt={6}>
                  <Button
                    color="white"
                    bg="#712EFF"
                    borderRadius="12px"
                    w="auto"
                    leftIcon={<FaRegArrowAltCircleUp />}
                    px="60px"
                    py="12px"
                    onClick={() =>
                      generateImages(currentFileName, selectedImageAspectRatio)
                    }
                    isDisabled={
                      data?.upscaled ||
                      isWebSocketProcessing ||
                      Boolean(imageUrl?.length)
                    }
                    _hover={
                      data?.upscaled ||
                      isWebSocketProcessing ||
                      Boolean(imageUrl?.length)
                        ? {}
                        : { opacity: 0.9 }
                    }
                  >
                    <Text fontSize="16px">
                      {`${data?.upscaled || imageUrl ? "Upscaled" : `Upscale (2 credits)`}`}
                    </Text>
                  </Button>
                  <Button
                    minW="45px"
                    minH="45px"
                    bg="red.500"
                    color="white"
                    borderRadius="full"
                    onClick={deleteOpen}
                    _hover={{ bg: "red.600" }}
                  >
                    <FaTrash size="14" />
                  </Button>
                  <Button
                    minW="45px"
                    minH="45px"
                    bg="blue.500"
                    color="white"
                    borderRadius="full"
                    onClick={async () => {
                      try {
                        const downloadUrl = imageUrl
                          ? isFlux
                            ? imageUrl
                            : `data:image/jpeg;base64,${imageUrl}`
                          : currentImage;

                          

                        const response = await fetch(downloadUrl);
                        const blob = await response.blob();
                        const blobUrl = URL.createObjectURL(blob);

                        const link = document.createElement("a");
                        link.href = blobUrl;
                        link.download =
                          currentFileName.split("/").pop() || "image.jpg";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        // Clean up the blob URL
                        URL.revokeObjectURL(blobUrl);

                        toast({
                          title: "Download Started",
                          status: "success",
                          duration: 3000,
                          isClosable: true,
                        });
                      } catch (error) {
                        console.error("Download error:", error);
                        toast({
                          title: "Download Failed",
                          description:
                            "Failed to download the image. Please try again.",
                          status: "error",
                          duration: 5000,
                          isClosable: true,
                        });
                      }
                    }}
                    _hover={{ bg: "blue.600" }}
                  >
                    <FaDownload size="14" />
                  </Button>
                </Flex>
              </Flex>

              {/* Right navigation button */}
              <Flex alignSelf="center" justifyContent="center" w="50px">
                {renderNextButton(false)}
              </Flex>
            </Flex>
          </Box>
        </ModalContent>
        <Modal size="xl" isOpen={isDeleteOpen} onClose={deleteClose}>
          <ModalOverlay />
          <ModalContent borderRadius={0} bg="#16181F" border="1px solid white">
            <ModalCloseButton />
            <Flex
              justify="center"
              w="100%"
              p={10}
              px={{ base: "30px", md: "80px" }}
              minH="300px"
              direction="column"
            >
              <Text
                fontSize={{ base: "28px", md: "36px" }}
                fontWeight={600}
                color="white"
              >
                Are you sure you want to delete this image?
              </Text>
              <Flex>
                <Button
                  mt="55px"
                  bg="white"
                  borderRadius="32px"
                  color="black"
                  onClick={handleDelete}
                >
                  Delete Image
                </Button>
              </Flex>
            </Flex>
          </ModalContent>
        </Modal>
      </Modal>
    </>
  );
};

export default PromptModal;
